import { ImportService } from '@amzn/awscat-portfolio-assessment-service-client';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Container, Grid, Hotspot, Link as PolarisLink, RadioGroup, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { Component } from 'react';
import { Link } from 'react-router-dom';

import { constants } from '../../../common/Localization';
import { ErrorWithResponse } from '../../../common/types';
import { format } from '../../../common/util';
import { HotspotIds } from '../../tutorials/HotspotIds';
import FileUpload from './common-components/FileUpload';
import { EXCEL_MAGIC_NUMBERS, IMPORT_FILE_SIZE_LIMIT_IN_MB, INPUT_DATA_FILE_TYPES,
    RESOURCE_TYPES, RESOURCE_TYPES_MAP, STAGE_VALUES } from './common-util/resources';
import { validateMagicNumber } from './common-util/util';

type Props = {
    portfolioId: string;
    type: string;
    onTypeChange: (type: string) => void;
    setStage: (stage: STAGE_VALUES) => void;
    setImportId: (importId: string) => void;
    setFileName: (fileName: string) => void;
} & FlashContextInterface;

interface State {
    loading: boolean;
}

const importService = new ImportService();
class ImportDefaultForm extends Component<Props, State> {
    state = {
        loading: false,
    };

    render() {
        if (this.state.loading) {
            return <Spinner size='large' />;
        }
        return (
            <Container
                header={
                    <h2>{constants.IMPORT_PORTFOLIO_DATA_HEADER}</h2>
                } disableContentPaddings>
                <Grid gridDefinition={[{colspan: 4}, {colspan: 8}]} disableGutters>
                    <Box padding='l'>
                        <span>{constants.SELECT_DATA_TYPE_HEADER}</span>
                        <RadioGroup value={this.props.type} items={RESOURCE_TYPES} onChange={this.onRadioChange} name='dataTypeButton'/>
                    </Box>
                    <Box id='upload-file-container' textAlign='center' margin='n' padding={{vertical:'xxl'}}>
                        <SpaceBetween size='xs'>
                            <h2>{RESOURCE_TYPES_MAP[this.props.type].label}</h2>
                            <FileUpload onFileUpload={this.onFileUpload} acceptTypes={INPUT_DATA_FILE_TYPES}/>

                            <ul className='no-list-style'>
                                <li>{constants.FILE_CAN_BE_IN_FORMAT}</li>
                                <li>
                                    <Hotspot hotspotId={HotspotIds.SAMPLE_FILES_DOWNLOAD} />
                                    <PolarisLink onFollow={() => importService.downloadSampleFile()} >{constants.SAMPLE_FILES_LINK}</PolarisLink>
                                    <span> {constants.AND} </span>
                                    <PolarisLink onFollow={() => importService.downloadTemplateFile()}>{constants.TEMPLATE_FILES_LINK}</PolarisLink>
                                    <span> {constants.AVAILABLE_FOR_DOWNLOAD}</span>
                                </li>
                                <li>{constants.REFER_TO_FAQ}<Link to='/faq' target='_blank'>{constants.FAQ}</Link>.</li>
                            </ul>
                        </SpaceBetween>
                    </Box>
                </Grid>
            </Container>

        );
    }

    onRadioChange = (e: any): void => {
        const value: string = e.detail.value;
        this.props.onTypeChange(value);
    };

    onFileUpload = async (file: any): Promise<void> => {
        const { portfolioId, type, setFileName } = this.props;
        if (file.size > IMPORT_FILE_SIZE_LIMIT_IN_MB * 1024 * 1024) {
            const errMsg = format(constants.ERR_FILE_SIZE_EXCEED_LIMIT, [IMPORT_FILE_SIZE_LIMIT_IN_MB.toString()]);
            this.props.flash.alert(FlashType.error, errMsg, constants.ERR_HEADERS.CANNOT_UPLOAD_FILE);
            return;
        }

        // Validate xls and xlsx files by magic numbers
        try {
            const isExcelFile = await validateMagicNumber(file, EXCEL_MAGIC_NUMBERS);
            const isCSVFile = file.type === INPUT_DATA_FILE_TYPES[0];
            if (!isCSVFile && !isExcelFile) {
                this.props.flash.alert(FlashType.error, constants.ERR_UNSUPPORTED_FILE, constants.ERR_HEADERS.CANNOT_UPLOAD_FILE);
                return;
            }
        } catch (err) {
            this.props.flash.alert(FlashType.error, (err as Error).message, constants.ERR_HEADERS.CANNOT_UPLOAD_FILE);
            return;
        }

        setFileName(file?.name);
        this.setLoading(true);
        try {
            // TODO: phase 2 retrieve importID and presigned URL; upload to S3
            const result = await importService.uploadFile(portfolioId, type, file);
            const importId = result?.importId;
            if (!importId) {
                throw new Error(constants.ERR_HEADERS.CANNOT_START_IMPORT);
            }
            this.props.setImportId(importId);
            this.setLoading(false);
            this.props.setStage(STAGE_VALUES.WIZARD);
        } catch (err) {
            this.props.flash.alert(FlashType.error, (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message,
                constants.ERR_HEADERS.CANNOT_UPLOAD_FILE);
            this.setLoading(false);
        }
    };

    setLoading = (loading: boolean): void => {
        this.setState({
            loading,
        });
    };
}

export default withFlashContext(ImportDefaultForm);
